import React from 'react';
import styled from 'styled-components';
import { Banner, ButtonText, Tooltip } from 'components';
import { lineHeight, theme, toRem } from 'design';
import { IPayPeriod } from 'types';
import { getPayPeriodBannerState, PayPeriodBannerState } from 'utils/payPeriods';

const PAY_PERIOD_BANNER_LABEL: Record<PayPeriodBannerState, string> = {
  [PayPeriodBannerState.OPEN]: 'Pay Period Open',
  [PayPeriodBannerState.IN_PROGRESS]: 'Pay Period Open - In Progress',
  [PayPeriodBannerState.RECOMMEND_CLOSING]: 'Pay Period Open - Recommend Closing',
  [PayPeriodBannerState.CLOSED]: 'Pay Period Closed',
};

// An in-content notice: rounded, and one size at every breakpoint unlike the page-level banners.
const Layout = styled(Banner)`
  flex-flow: row wrap;
  gap: ${toRem(theme.space.xs)};
  min-height: 0;
  margin-bottom: ${toRem(theme.space.lg)};
  padding: ${toRem(theme.space.sm)};
  border-radius: ${toRem(theme.space.xs)};
  font-size: ${toRem(theme.font.size.md)};
  line-height: ${lineHeight(theme.font.size.md)};
`;

// The action reads as part of the sentence: primary color, no underline until hovered.
const Action = styled(ButtonText)`
  text-decoration: none;
  color: ${theme.color.primary[500]};

  &:hover {
    color: ${theme.color.primary[500]};
    text-decoration: underline;
  }

  &:disabled {
    color: ${theme.color.neutral[400]};
    cursor: default;
    text-decoration: none;
    pointer-events: none;
  }
`;

interface IPayPeriodBannerProps {
  payPeriod: IPayPeriod;
  // YYYY-MM-DD in the site's timezone; decides between open, in progress and recommend closing.
  today: string;
  onClosePeriod: () => void;
  onReopenPeriod: () => void;
  canClose?: boolean;
  canReopen?: boolean;
  dataCy?: string;
}

// States the selected pay period above the payee report and offers the one action that applies to it.
const PayPeriodBanner = ({ payPeriod, today, onClosePeriod, onReopenPeriod, canClose = false, canReopen = false, dataCy = 'pay-period-banner' }: IPayPeriodBannerProps) => {
  const state = getPayPeriodBannerState(payPeriod, today);
  const action =
    state === PayPeriodBannerState.CLOSED
      ? { label: 'Reopen Pay Period', onClick: onReopenPeriod, isAllowed: canReopen, askAdmin: 'Ask an Org Admin to reopen it' }
      : { label: 'Close Pay Period', onClick: onClosePeriod, isAllowed: canClose, askAdmin: 'Ask an admin to close it' };

  return (
    <Layout appearance={state === PayPeriodBannerState.RECOMMEND_CLOSING ? 'caution' : 'neutral'} dataCy={dataCy} data-state={state}>
      <span data-cy={`${dataCy}-label`}>{PAY_PERIOD_BANNER_LABEL[state]}&nbsp;-</span>
      <span data-tooltip-id={`${dataCy}-blocked`}>
        <Action label={action.label} fontSize={theme.font.size.md} fontWeight={theme.font.weight.bold} disabled={!action.isAllowed} onClick={action.onClick} data-cy={`${dataCy}-action`} />
        {!action.isAllowed && (
          <Tooltip id={`${dataCy}-blocked`} place="right">
            {action.askAdmin}
          </Tooltip>
        )}
      </span>
    </Layout>
  );
};

export default PayPeriodBanner;
